import React, { useState, useEffect } from "react";
import "../styles/EventsOrganises.css";
import cover from "../assets/cover.jpg";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { supabase } from '../services/supabase';

export default function EventsOrganises() {
	const [data, setData] = useState({
		title: '',
		subtitle: '',
		events: []
	});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		fetchEventsData();
	}, []);

	const fetchEventsData = async () => {
		try {
			const { data: eventsData, error } = await supabase
				.from('events_organises')
				.select('*')
				.single();

			if (error) {
				console.error('Supabase error:', error);
				setError('Failed to load events content');
				return;
			}

			if (eventsData) {
				setData({
					title: eventsData.title || '',
					subtitle: eventsData.subtitle || '',
					events: eventsData.events || []
				});
			}
		} catch (error) {
			console.error('Error fetching events data:', error);
			setError('Failed to load events content');
		} finally {
			setLoading(false);
		}
	};

	// Small internal card component for clarity
	function EventCard({ event }) {
		return (
			<article className="eo-card" aria-labelledby={`${event.id}-title`} role="listitem">
				{/* image / media header */}
				<div className="eo-card-media">
					<img src={event.thumb || cover} alt={event.title} />
					<span className="eo-tag eo-tag-media">{event.tag}</span>
				</div>

				<h4 className="eo-title" id={`${event.id}-title`}>{event.title}</h4>

				{event.lead && <p className="eo-lead">{event.lead}</p>}

				{event.sections?.map((sec, idx) => {
					const List = sec.type === "ol" ? "ol" : "ul";
					const className = sec.type === "ol" ? "eo-list" : "eo-list";
					return (
						<section key={idx} aria-label={sec.role}>
							{sec.role}
							<List className={className}>
								{sec.items?.map((it, i) => (
									<li key={i}>{it}</li>
								))}
							</List>
						</section>
					);
				})}

				{event.meta && (
					<ul className="eo-meta" aria-hidden="false">
						{event.meta.map((m, i) => (
							<li key={i} className="eo-chip">{m}</li>
						))}
					</ul>
				)}

				{event.small && <p className="eo-small">{event.small}</p>}
			</article>
		);
	}

	if (loading) {
		return <div className="eo-section loading">Loading events...</div>;
	}

	if (error) {
		return <div className="eo-section error">Error: {error}</div>;
	}

	if (!data.events.length) {
		return <div className="eo-section empty">No events available</div>;
	}

	return (
		<section className="eo-section" aria-labelledby="eo-title">
			<div className="eo-container">
				<header className="eo-header">
					<h2 id="eo-title">{data.title}</h2>
					<p className="eo-sub">{data.subtitle}</p>
				</header>

				<Swiper
					modules={[Pagination, Autoplay]}
					spaceBetween={5}
					slidesPerView={3}
					centeredSlides={true}
					pagination={{ 
						clickable: true,
						el: '.swiper-pagination-custom',
					}}
					autoplay={{
						delay: 4000,
						disableOnInteraction: false,
					}}
					slideToClickedSlide={true}
					allowTouchMove={true}
					grabCursor={true}
					breakpoints={{
						320: {
							slidesPerView: 1,
							spaceBetween: 20,
						},
						768: {
							slidesPerView: 2,
							spaceBetween: 30,
						},
						1024: {
							slidesPerView: 3,
							spaceBetween: 40,
						},
					}}
					className="eo-swiper"
					style={{
						"--swiper-slide-opacity": "0.4",
						"--swiper-slide-scale": "0.8",
						paddingBottom: "50px",
					}}
					onSlideChange={(swiper) => {
						swiper.slides.forEach((slide, index) => {
							if (index === swiper.activeIndex) {
								slide.style.opacity = "1";
								slide.style.transform = "scale(1)";
								slide.style.zIndex = "2";
							} else {
								slide.style.opacity = "0.4";
								slide.style.transform = "scale(0.8)";
								slide.style.zIndex = "1";
							}
						});
					}}
					onInit={(swiper) => {
						swiper.slides.forEach((slide, index) => {
							slide.style.transition = "all 0.3s ease";
							if (index === swiper.activeIndex) {
								slide.style.opacity = "1";
								slide.style.transform = "scale(1)";
								slide.style.zIndex = "2";
							} else {
								slide.style.opacity = "0.4";
								slide.style.transform = "scale(0.8)";
								slide.style.zIndex = "1";
							}
						});
					}}
				>
					{data.events.map((ev) => (
						<SwiperSlide key={ev.id} style={{ height: "auto", marginTop: "20px", marginBottom: "20px", cursor: "pointer" }}>
							<EventCard event={ev} />
						</SwiperSlide>
					))}
					<div className="swiper-pagination-custom" style={{ marginTop: "30px", cursor: "pointer" }}></div>
				</Swiper>
			</div>
		</section>
	);
}
